import React, { useEffect, useState, useCallback } from 'react';

interface AssetPreloaderProps {
  images?: string[];
  fonts?: string[];
  onLoadComplete?: () => void;
  onProgress?: (loaded: number, total: number) => void;
  showProgress?: boolean;
  children?: React.ReactNode;
}

interface PreloadState {
  loaded: number;
  total: number;
  isComplete: boolean;
  errors: string[];
}

const AssetPreloader: React.FC<AssetPreloaderProps> = ({
  images = [],
  fonts = [],
  onLoadComplete,
  onProgress,
  showProgress = false,
  children
}) => {
  const [preloadState, setPreloadState] = useState<PreloadState>({
    loaded: 0,
    total: images.length + fonts.length,
    isComplete: false,
    errors: []
  });

  // Preload images
  const preloadImage = useCallback((src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        setPreloadState(prev => ({
          ...prev,
          loaded: prev.loaded + 1
        }));
        resolve();
      };
      
      img.onerror = () => {
        setPreloadState(prev => ({
          ...prev,
          loaded: prev.loaded + 1,
          errors: [...prev.errors, `Failed to load image: ${src}`]
        }));
        reject(new Error(`Failed to load image: ${src}`));
      };
      
      img.src = src;
    });
  }, []);

  // Preload fonts
  const preloadFont = useCallback((fontFamily: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!document.fonts) {
        // Fallback for browsers without Font Loading API
        setPreloadState(prev => ({
          ...prev,
          loaded: prev.loaded + 1
        }));
        resolve();
        return;
      }

      // System fonts (system-ui, -apple-system, BlinkMacSystemFont) are native OS fonts
      // and don't need to be loaded - they should just be used directly
      const systemFonts = ['system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Helvetica Neue', 'Arial'];
      if (systemFonts.some(sf => fontFamily.toLowerCase().includes(sf.toLowerCase()))) {
        // System fonts are always available, just count as loaded
        setPreloadState(prev => ({
          ...prev,
          loaded: prev.loaded + 1
        }));
        resolve();
        return;
      }

      const font = new FontFace(fontFamily, `url(${fontFamily})`);
      
      font.load()
        .then(() => {
          document.fonts.add(font);
          setPreloadState(prev => ({
            ...prev,
            loaded: prev.loaded + 1
          }));
          resolve();
        })
        .catch((error) => {
          setPreloadState(prev => ({
            ...prev,
            loaded: prev.loaded + 1,
            errors: [...prev.errors, `Failed to load font: ${fontFamily}`]
          }));
          reject(error);
        });
    });
  }, []);

  // Main preload function
  const preloadAssets = useCallback(async () => {
    if (preloadState.total === 0) {
      setPreloadState(prev => ({ ...prev, isComplete: true }));
      return;
    }

    const imagePromises = images.map(preloadImage);
    const fontPromises = fonts.map(preloadFont);
    
    try {
      await Promise.allSettled([...imagePromises, ...fontPromises]);
    } catch (error) {
      console.warn('Some assets failed to preload:', error);
    }
    
    setPreloadState(prev => ({ ...prev, isComplete: true }));
  }, [images, fonts, preloadImage, preloadFont, preloadState.total]);

  // Start preloading on mount
  useEffect(() => {
    preloadAssets();
  }, [preloadAssets]);

  // Call progress callback
  useEffect(() => {
    if (onProgress) {
      onProgress(preloadState.loaded, preloadState.total);
    }
  }, [preloadState.loaded, preloadState.total, onProgress]);

  // Call completion callback
  useEffect(() => {
    if (preloadState.isComplete && onLoadComplete) {
      onLoadComplete();
    }
  }, [preloadState.isComplete, onLoadComplete]);

  // Progress component
  const ProgressIndicator = () => {
    const percentage = preloadState.total > 0 
      ? Math.round((preloadState.loaded / preloadState.total) * 100)
      : 100;

    return (
      <div className="fixed inset-0 bg-navy-950 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-navy-800 rounded-lg p-6 max-w-sm w-full mx-4">
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold text-white">Loading Assets</h3>
            <p className="text-sm text-navy-200">
              {preloadState.loaded} of {preloadState.total} assets loaded
            </p>
          </div>
          
          <div className="w-full bg-navy-700 rounded-full h-2 mb-4">
            <div 
              className="bg-gold-500 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${percentage}%` }}
            />
          </div>
          
          <div className="text-center text-sm text-navy-300">
            {percentage}% complete
          </div>
          
          {preloadState.errors.length > 0 && (
            <div className="mt-4 text-xs text-red-400">
              <details>
                <summary className="cursor-pointer">
                  {preloadState.errors.length} error(s) occurred
                </summary>
                <ul className="mt-2 space-y-1">
                  {preloadState.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Show progress indicator if requested and not complete
  if (showProgress && !preloadState.isComplete) {
    return <ProgressIndicator />;
  }

  // Render children only when preloading is complete or if no assets to preload
  return preloadState.isComplete || preloadState.total === 0 ? (
    <>{children}</>
  ) : null;
};

// Hook for using asset preloader
export const useAssetPreloader = (assets: { images?: string[]; fonts?: string[] }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });
  const [errors, setErrors] = useState<string[]>([]);

  const handleProgress = useCallback((loaded: number, total: number) => {
    setProgress({ loaded, total });
  }, []);

  const handleComplete = useCallback(() => {
    setIsLoading(false);
  }, []);

  return {
    isLoading,
    progress,
    errors,
    PreloaderComponent: (
      <AssetPreloader
        images={assets.images}
        fonts={assets.fonts}
        onProgress={handleProgress}
        onLoadComplete={handleComplete}
        showProgress={isLoading}
      />
    )
  };
};

// Critical resource preloader for above-the-fold content
// Note: System fonts (system-ui, -apple-system, etc.) don't need preloading
export const CriticalResourcePreloader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const criticalAssets = {
    images: [
      // Add critical images that appear above the fold
    ],
    fonts: [
      // Only add custom web fonts here that need to be loaded
      // System fonts like 'system-ui', '-apple-system', 'BlinkMacSystemFont' are handled automatically
    ]
  };

  return (
    <AssetPreloader
      images={criticalAssets.images}
      fonts={criticalAssets.fonts}
      showProgress={false} // Don't show progress for critical resources
    >
      {children}
    </AssetPreloader>
  );
};

export default AssetPreloader;
